import {useQuery} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import type {JobDefinition, JobExecution, TaskExecutionTreeItem} from '@/types/background-jobs'

export const EXECUTION_KEY = 'execution'
export const EXECUTION_TASKS_KEY = 'execution-tasks'
export const JOB_FOR_EXECUTION_KEY = 'job'

export interface UseExecutionResult {
    execution: JobExecution | undefined
    job: JobDefinition | undefined
    taskExecutions: TaskExecutionTreeItem[]
    isExecutionLoading: boolean
    executionError: Error | null
}

/**
 * Composite read hook for the ExecutionDetail page — fetches the execution
 * record, its parent job (for the title link), and the flat task-execution
 * list in three independent queries. Failure of the parent-job fetch does
 * not knock out the execution view.
 */
export function useExecution(id: string | undefined): UseExecutionResult {
    const services = useServices()
    const enabled = !!id

    const executionQuery = useQuery({
        queryKey: [EXECUTION_KEY, id],
        queryFn: () => services.executions.detail(id!),
        enabled,
    })

    const jobQuery = useQuery({
        queryKey: [JOB_FOR_EXECUTION_KEY, executionQuery.data?.jobId],
        queryFn: () => services.jobs.detail(executionQuery.data!.jobId),
        enabled: !!executionQuery.data?.jobId,
    })

    const tasksQuery = useQuery({
        queryKey: [EXECUTION_TASKS_KEY, id],
        queryFn: () => services.jobs.executionTasks(id!),
        enabled,
    })

    return {
        execution: executionQuery.data,
        job: jobQuery.data,
        taskExecutions: tasksQuery.data ?? [],
        isExecutionLoading: executionQuery.isLoading,
        executionError: (executionQuery.error as Error | null) ?? null,
    }
}
